import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconeColorido } from "@/components/comecar/selecao/IconeColorido";
import { iconeDaCategoria } from "@/components/comecar/selecao/icones-da-selecao";

/** Foto quadrada do cartão, como a do Meu perfil; sem foto, a inicial no fundo escuro. */
export function FotoDoMembro({ src, nome }: { src: string | null; nome: string }) {
  return src ? (
    <img src={src} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover shadow-sm" />
  ) : (
    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-zinc-900 text-xl font-semibold text-white shadow-sm">
      {nome.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/** Até duas especialidades em chip com o ícone; o resto vira "+N". */
export function ChipsDeEspecialidade({ especialidades }: { especialidades: string[] }) {
  if (especialidades.length === 0) {
    // Some, e o cartão dá a impressão de que a informação não existe — em vez de faltar cadastro.
    return <p className="text-[11px] italic text-zinc-400 dark:text-muted-foreground/60">Especialidade não informada</p>;
  }
  const visiveis = especialidades.slice(0, 2);
  const resto = especialidades.length - visiveis.length;
  return (
    <div className="flex min-w-0 flex-wrap gap-1" title={especialidades.join(" · ")}>
      {visiveis.map((e) => (
        <span
          key={e}
          className="inline-flex max-w-full items-center gap-1 truncate rounded-md border border-zinc-200 bg-white py-0.5 pl-0.5 pr-1.5 text-[11px] font-medium text-zinc-700 dark:border-border dark:bg-card dark:text-foreground"
        >
          <IconeColorido {...iconeDaCategoria(e)} tamanho="sm" />
          <span className="truncate">{e}</span>
        </span>
      ))}
      {resto > 0 && (
        <span className="inline-flex items-center rounded-md border border-zinc-200 bg-white px-1.5 text-[11px] font-medium text-zinc-500 dark:border-border dark:bg-card dark:text-muted-foreground">
          +{resto}
        </span>
      )}
    </div>
  );
}

/** Zero fica visível, mas sem peso: ícone e número apagados. O olho pula os zerados e para em
 *  quem tem carga, que é a leitura que interessa. */
export function NumeroDoMembro({ valor, rotulo, icone: Icone, cor }: { valor: number; rotulo: string; icone: LucideIcon; cor: string }) {
  const vazio = valor === 0;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-zinc-100 bg-white px-2.5 py-2 dark:border-border dark:bg-card">
      <span
        className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-md sm:flex"
        style={vazio ? undefined : { backgroundColor: `${cor}1a`, color: cor }}
      >
        <Icone className={cn("h-3.5 w-3.5", vazio && "text-zinc-300 dark:text-muted-foreground/40")} />
      </span>
      <span className="min-w-0">
        <span className={cn("block text-base font-bold leading-tight tabular-nums", vazio ? "text-zinc-300 dark:text-muted-foreground/40" : "text-zinc-900 dark:text-foreground")}>
          {valor}
        </span>
        <span className="block truncate text-[10px] leading-tight text-zinc-500 dark:text-muted-foreground">{rotulo}</span>
      </span>
    </div>
  );
}
